import type {
  ShopifyCollectionRef,
  ShopifyDiscountValueType,
  ShopifyMinimumRequirementType,
  ShopifyProductRef,
  ShopifyTargetType,
} from '@discovr/features/shopify';
import { shopifyDiscountValueCopy, shopifyVariantsCopy } from '@discovr/features/copy';
import { shopifyActionsCopy } from '@discovr/features/copy';
import { COMBINES_WITH_OPTIONS } from './shopifyCombinesWithOptions';
import { formatStoreMoney } from './shopifyCurrency';
import { fmtStoreDateTime } from './shopifyStoreTime';

/** The "Discount" summary row — "15% off" or "₹500 off", whichever
 *  value_type is actually active. Single source for the campaign config
 *  form's and the coupon edit modal's SUMMARY sidebars. `currencyCode` is the
 *  ISO 4217 code the amount is denominated in — the coupon's own snapshot for
 *  a minted coupon, the connected store's for the config form (see
 *  resolveCurrencyCode); ignored for percentages. */
export function discountValueSummary(
  valueType: ShopifyDiscountValueType,
  pct: number,
  amount: number,
  currencyCode: string,
  appliesOnEachItem = false,
): string {
  if (valueType !== 'fixed_amount') return `${pct}% off`;
  const money = formatStoreMoney(amount, currencyCode);
  return appliesOnEachItem ? shopifyDiscountValueCopy.eachItemSummary(money) : `${money} off`;
}

/** Shared summary-sidebar formatters — used by both ShopifyCouponConfigForm
 *  (campaign config form, `CampaignShopifyConfigInput` shape) and
 *  ShopifyConfigEditModalBody (coupon edit modal, local component state).
 *  Parameterized over plain values rather than either input shape, so each
 *  caller just passes the fields it already has instead of adapting its
 *  shape to match the other's. Previously duplicated near-verbatim in both
 *  files. */

export function targetSummary(
  target: ShopifyTargetType,
  products: ShopifyProductRef[],
  collections: ShopifyCollectionRef[],
  variants: readonly unknown[] = [],
): string {
  if (target === 'entire_store') return 'Entire store';
  if (target === 'product') {
    return products.length + variants.length === 0
      ? 'No products selected'
      : shopifyVariantsCopy.summary(products.length, variants.length);
  }
  return collections.length === 0
    ? 'No collections selected'
    : `${collections.length} collection${collections.length === 1 ? '' : 's'}`;
}

export function minimumRequirementSummary(
  type: ShopifyMinimumRequirementType,
  value: number,
  currencyCode: string,
): string {
  if (type === 'amount') return `${formatStoreMoney(value || 0, currencyCode)}+ subtotal`;
  if (type === 'quantity') return `${value || 0}+ items`;
  return 'None';
}

export function combinesWithSummary(order: boolean, product: boolean, shipping: boolean): string {
  const values: Record<(typeof COMBINES_WITH_OPTIONS)[number]['key'], boolean> = {
    orderDiscounts: order,
    productDiscounts: product,
    shippingDiscounts: shipping,
  };
  const bits = COMBINES_WITH_OPTIONS.filter((opt) => values[opt.key]).map((opt) =>
    opt.label.replace('Combines with ', ''),
  );
  return bits.length === 0 ? 'None' : bits.join(', ');
}

/** The "Usage limit" summary row — mirrors Shopify's own "No usage limits" /
 *  "Limit N uses" / "One per customer" copy. */
export function usageLimitSummary(
  usageLimit: number | undefined,
  appliesOncePerCustomer: boolean,
): string {
  const bits: string[] = [];
  if (usageLimit) bits.push(shopifyActionsCopy.totalUses(usageLimit));
  if (appliesOncePerCustomer) bits.push('one per customer');
  return bits.length === 0 ? 'No usage limits' : bits.join(', ');
}

/** The "Active dates" summary row — mirrors the Active dates card's own copy
 *  for the empty/partial/full states. Dates read in the store's zone with its
 *  abbreviation ("UTC" when the zone is unknown). */
export function activeDatesSummary(
  start: string | undefined,
  end: string | undefined,
  storeTimezone?: string,
): string {
  const at = (iso: string) => fmtStoreDateTime(iso, storeTimezone);
  if (start && end) return `${at(start)} – ${at(end)}`;
  if (start) return `From ${at(start)}`;
  if (end) return `Until ${at(end)}`;
  return 'Runs until stopped';
}
